import ErrorHandlingForm from "../../../components/formValidation/ErrorHandlingFrom";
import Stack from "@mui/material/Stack";
import CustomTextField from "../../../components/formValidation/CustomTextField";
import React, {useEffect, useRef, useState} from "react";
import {Button, FormControl, InputLabel, MenuItem, Typography} from "@mui/material";
import CustomAutocomplete from "../../../components/formValidation/CustomAutocomplete";
import {generateUUID} from "../../../utils/utils";
import AddIcon from "@mui/icons-material/Add";
import CustomSelect from "../../../components/formValidation/CustomSelect";
import {countries} from "../../../utils/countries";
import CustomDateRangePicker from "../../../components/formValidation/CustomDateRangePicker";
import useDataModel from "../../../hooks/dataModel";
import useCallDataApi from "../../../hooks/data";
import DinoDialog from "../../../components/Dialog";
import NewTourGuideData from "../../tourGuides/NewTourGuideData";
import NewTourOfficeData from "../../tourOffices/NewTourOfficeData";
import {validateRequired} from "../../../components/formValidation/validators";
import Loading from "../../../components/Loading";
import {deliveryTypes} from "../../../utils/enums";

const GeneralData = ({data, setData, reference}) => {
    const [loading, setLoading] = useState(false)
    const [isTourGuideDialogOpen, toggleTourGuideDialog] = useState(false)
    const [isTourOfficeDialogOpen, toggleTourOfficeDialog] = useState(false)

    const newTourGuideForm = useRef(null);
    const newTourOfficeForm = useRef(null);

    const {fetchModels: fetchUsers, models: users} = useDataModel(useCallDataApi("users"))
    const {
        createModel: createTourOffice,
        postModel: postTourOffice,
        models: tourOffices,
        setModels: setTourOffices
    } = useDataModel(useCallDataApi("tour-offices"))
    const {
        createModel: createTourGuide,
        postModel: postTourGuide,
        models: tourGuides,
        setModels: setTourGuides
    } = useDataModel(useCallDataApi("tour-guides"))
    const {
        postModel: postContactPersons,
        models: contactPersons,
        setModels: setContactPersons
    } = useDataModel(useCallDataApi("contact-person"))

    useEffect(() => {
        fetchUsers()
    }, [])


    useEffect(() => {
        postTourOffice('get_for_user', {user_id: data?.ordered_by}, models => setTourOffices(models))
        postTourGuide('get_for_user', {user_id: data?.ordered_by}, models => setTourGuides(models))
    }, [data?.ordered_by])

    useEffect(() => {
        if (data?.tour_office) postContactPersons(
            'get_for_tour_office',
            {tour_office_id: data?.tour_office},
            models => setContactPersons(models)
        )
        else setData(prev => ({...data, contact_person: null}))
    }, [data?.tour_office])

    const [errors, setErrors] = useState({
        internal_comment: false,
        ordered_by: false,
        tour_office: false,
        contact_person: false,
        tour_guide: false,
        usage_country: false,
        delivery_date: false,
        return_date: false,
    })

    const requiredIfThirdParty = value => {
        const deliveryCompanies = [deliveryTypes.DHL, deliveryTypes.GLS];
        const sentByThirdParty = deliveryCompanies.includes(data?.delivery_type) || deliveryCompanies.includes(data?.return_type);
        if (!sentByThirdParty) return {
            valid: true,
            error: ''
        }
        return {
            valid: Boolean(value),
            error: 'Ha a szállítás vagy a visszaszállítás módja csomagküldő cég, akkor kötelező megadni!'
        }
    }

    const validators = {
        internal_comment: [],
        ordered_by: [],
        tour_office: [validateRequired],
        contact_person: [],
        tour_guide: [value => requiredIfThirdParty(value)],
        usage_country: [validateRequired],
        delivery_date: [validateRequired],
        return_date: [validateRequired],
    }

    return <ErrorHandlingForm
        data={data}
        setData={setData}
        errors={errors}
        setErrors={setErrors}
        validators={validators}
        reference={reference}
    >
        <Stack spacing={1}>

            <Typography variant='body'>Megjegyzés: {data.comment || '-'}</Typography>

            <CustomTextField
                multiline
                rows={3}
                name="internal_comment"
                label="Belsős megjegyzés"
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
            />

            <FormControl>
                <CustomAutocomplete
                    options={users}
                    getOptionLabel={(option) => {
                        if (typeof option === "number") {
                            const to = users.find(t => t?.id === option)
                            return to?.email || ""
                        }
                        return option?.email || ""
                    }}
                    label='Partner e-mail'
                    renderOption={(props, option) => (
                        <li {...props} key={option?.id || generateUUID()}>
                            {option?.email}
                        </li>
                    )}
                    fieldToUse='id'
                    name="ordered_by"
                    data={data}
                    sideEffect={() => setData(prev => ({...prev, tour_office: null}))}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                />
            </FormControl>

            <FormControl>
                <CustomAutocomplete
                    options={tourOffices}
                    getOptionLabel={(option) => {
                        if (typeof option === "number") {
                            const to = tourOffices.find(t => t?.id === option)
                            return to?.name || to?.name_short || ""
                        }
                        return option?.name || option?.short_name || ""
                    }}
                    label='Megrendelő'
                    renderOption={(props, option) => (
                        <li {...props} key={option?.id}>
                            {option?.name || option?.short_name}
                        </li>
                    )}
                    fieldToUse='id'
                    name="tour_office"
                    sideEffect={() => setData(prev => ({...prev, contact_person: null}))}
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                />
            </FormControl>

            <Button
                onClick={() => toggleTourOfficeDialog(true)}
                variant="outlined"
                endIcon={<AddIcon/>}
                color="success">
                Megrendelő
            </Button>


            <FormControl>
                <CustomAutocomplete
                    options={contactPersons}
                    getOptionLabel={(option) => {
                        if (typeof option === "number") {
                            const c = contactPersons.find(t => t?.id === option)
                            return `${c?.first_name} ${c?.last_name} ${c?.phone}` || ""
                        }
                        return option?.name || ""
                    }}
                    label='Kapcsolattartó'
                    renderOption={(props, option) => (
                        <li {...props} key={option?.id || generateUUID()}>
                            {`${option?.first_name} ${option?.last_name} ${option?.phone}`}
                        </li>
                    )}
                    fieldToUse='id'
                    name="contact_person"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                />
            </FormControl>

            <FormControl>
                <CustomAutocomplete
                    options={tourGuides}
                    getOptionLabel={(option) => {
                        if (typeof option === "number") {
                            const to = tourGuides.find(t => t?.id === option)
                            return to ? `${to.first_name} ${to.last_name} ${to?.phone}` : ""
                        }
                        return option ? `${option.first_name} ${option.last_name} ${option?.phone}` : ""
                    }}
                    label='Csoportkísérő'
                    renderOption={(props, option) => (
                        <li {...props} key={option?.id || generateUUID()}>
                            {`${option.first_name} ${option.last_name} ${option?.phone}` || "-"}
                        </li>
                    )}
                    fieldToUse='id'
                    name="tour_guide"
                    data={data}
                    setData={setData}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                />
            </FormControl>

            <Button
                onClick={() => {
                    toggleTourGuideDialog(true)
                }}
                variant="outlined"
                endIcon={<AddIcon/>}
                color="success">
                Csoportkísérő
            </Button>

            <FormControl>
                <InputLabel id="usage-country-label">Célország</InputLabel>
                <CustomSelect
                    reference={reference}
                    errors={errors}
                    setErrors={setErrors}
                    validators={validators}
                    data={data}
                    setData={setData}
                    id="usage_country"
                    name="usage_country"
                    label="Célország"
                    labelId="usage-country-label"
                >
                    {countries?.map(country => <MenuItem key={country?.code} value={country?.code}>
                        <img
                            src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                            alt={country?.label}
                            style={{width: 24, height: 16, marginRight: 8}}
                        />
                        {country?.label}
                    </MenuItem>)}
                </CustomSelect>
            </FormControl>

            <CustomDateRangePicker
                reference={reference}
                errors={errors}
                setErrors={setErrors}
                validators={validators}
                data={data}
                setData={setData}
                inputFormat="yyyy-MM-dd"
                name="delivery_date|return_date"
                aria-label="Rendelés dátumai"
                minDate={Date.now()}
            />
        </Stack>


        <DinoDialog
            title="Új csoportkísérő"
            open={isTourGuideDialogOpen}
            handleClose={() => toggleTourGuideDialog(false)}
            actions={<Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
                <Button size='large' variant='outlined' onClick={() => toggleTourGuideDialog(false)}>Mégsem</Button>
                <Button size='large' variant='outlined' color='success' onClick={() => {
                    const data = newTourGuideForm.current.getData()
                    if (Object.entries(data).length > 0) {
                        toggleTourGuideDialog(false)
                        createTourGuide(data)
                    }
                }}>Hozzáadás</Button>
            </Stack>}>
            <NewTourGuideData reference={newTourGuideForm}/>
        </DinoDialog>


        <DinoDialog
            title="Új megrendelő"
            open={isTourOfficeDialogOpen}
            handleClose={() => toggleTourOfficeDialog(false)}
            actions={<Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
                <Button size='large' variant='outlined' onClick={() => toggleTourOfficeDialog(false)}>Mégsem</Button>
                <Button size='large' variant='outlined' color='success' onClick={() => {
                    const data = newTourOfficeForm.current.getData()
                    if (Object.entries(data).length > 0) {
                        toggleTourOfficeDialog(false)
                        createTourOffice(data)
                    }
                }}>Hozzáadás</Button>
            </Stack>}>
            <NewTourOfficeData reference={newTourOfficeForm}/>
        </DinoDialog>
        <Loading isLoading={loading}/>
    </ErrorHandlingForm>
}

export default GeneralData